import { BetStatus, BetslipStatus, BettingType, MenuItemType } from '../@types';
import { TFunction } from 'i18next';
import { SKIN, WALLET_TYPE } from '../types/enums';

export const SECOND = 1000;
export const MINUTE = 60 * SECOND;
export const MIN_SEARCH_LENGTH = 2;

export const FEATURED_SPORT_INTERVALS = {
  [SKIN.BET365]: ['today', '3'] as const,
  [SKIN.GOLA]: ['today', '2', '4'] as const,
  [SKIN.NEOBET]: ['today', '3'] as const,
} as const;

export const upcomingMatchesConfig = {
  INTERVALS: ['today', '3', '12', '24'] as const,
  DEFAULT_INTERVAL: '3',
} as const;

export const golaUpcomingMatchesConfig = {
  INTERVALS: ['today', '2', '3', '4', '12', '24'] as const,
  DEFAULT_INTERVAL: 'today',
} as const;

export const QUERY_KEYS = {
  featuredEvents: 'featured-events',
  topOfferTournaments: 'top-offer-tournaments',
  competitions: 'competitions',
  tournaments: 'tournaments',
  upcomingMatches: 'upcoming-matches',
  upcomingMatchesCount: 'upcoming-matches-count',
  sports: 'sports',
  betslip: 'betslip',
  betslips: 'betting-slips',
  publishedEvents: 'published-events',
  events: 'events',
  myBets: 'my-bets',
  myBetsCount: 'my-bets-count',
  copyBetslip: 'copy-betslip',
  betslipEvents: 'betslip-events',
  betHistory: 'bet-history',
  marketTypes: 'market-types',
  favoriteEventsCount: 'favorite-events-count',
  favoriteEvents: 'favorite-events',
  messages: 'messages-client',
  sentMessages: 'messages-client/sent',
  deletedMessages: 'messages-client/deleted',
  messageCount: 'messages-client/count',
  userSettings: 'user-settings',
  marketGroups: 'market-groups',
  balance: 'balance',
  firstDepositBonus: 'first-deposit-bonus',
  topData: 'top-data',
  sideData: 'side-data',
  footerData: 'footer-data',
  agents: 'agents',
  markets: 'markets',
  pendingBetslipsCount: 'pending-betslips-count',
  liveSports: 'live-sports',
  eventPartScores: 'event-part-scores',
  liveEventsCount: 'live-events-count',
  upcomingLiveEvents: 'upcoming-live-events',
  jackpots: 'jackpots',
  outcomes: 'outcomes',
  outrightMarkets: 'outright-markets',
  ticketConditions: 'ticket-conditions',
  cashout: 'cashout',
  marketplaceConfig: 'marketplaceConfig',
  searchResults: 'searchResults',
  eventStatistics: 'event-statistics',
  marketGroupCategories: 'market-group-categories',
  inPlay: 'in_play',
  preMatch: 'pre_match',
  customerData: 'customer-data',
  updatePassword: 'update-password',
  forgotPassword: 'forgot-password',
  resetPassword: 'reset-password',
  slipBonusCampaigns: 'slip-bonus-campaigns',
  ftbVirtualCourtToken: 'ftb-virtual-court-token',
  customerSearch: 'customer/search',
  userDetails: 'user-details',
  balanceUserList: 'agent/users/totals',
  walletTransactions: 'wallet-transactions',
  reports: 'reports',
  walletTransactionTotals: 'wallet-transaction-totals',
  geoLocation: 'geo-location',
  profitData: 'profit-data',
  casinoReports: 'casino-reports',
  casinoCategories: 'casino-categories',
  casinoProviders: 'casino-providers',
  casinoGames: 'casino-games',
  casinoGameUrl: 'casino-game-url',
  casinoTransactionsHistory: 'casino-transactions-history',
  cryptoExchangeRate: 'crypto-exchange-rate',
  welcomeBonusAvailability: 'welcome-bonus-availability',
} as const;

export const getLocalizedBettingTypeMap = (t: TFunction): Record<BettingType, string> => ({
  Single: t('single'),
  Double: t('double'),
  Treble: t('treble'),
  '4 Fold': t('fourFold'),
  '5 Fold': t('fiveFold'),
  '6 Fold': t('sixFold'),
  '7 Fold': t('sevenFold'),
  '8 Fold': t('eightFold'),
  '9 Fold': t('nineFold'),
  '10 Fold': t('tenFold'),
  '11 Fold': t('elevenFold'),
  '12 Fold': t('twelveFold'),
  '13 Fold': t('thirteenFold'),
  '14 Fold': t('fourteenFold'),
  '15 Fold': t('fifteenFold'),
  '16 Fold': t('sixteenFold'),
  '17 Fold': t('seventeenFold'),
  '18 Fold': t('eighteenFold'),
  '19 Fold': t('nineteenFold'),
  '20 Fold': t('twentyFold'),
});

export const BETTING_TYPE_HIT_COUNT_MAP: Record<BettingType, number> = {
  Single: 1,
  Double: 2,
  Treble: 3,
  '4 Fold': 4,
  '5 Fold': 5,
  '6 Fold': 6,
  '7 Fold': 7,
  '8 Fold': 8,
  '9 Fold': 9,
  '10 Fold': 10,
  '11 Fold': 11,
  '12 Fold': 12,
  '13 Fold': 13,
  '14 Fold': 14,
  '15 Fold': 15,
  '16 Fold': 16,
  '17 Fold': 17,
  '18 Fold': 18,
  '19 Fold': 19,
  '20 Fold': 20,
};

export const BETSLIP_WIDTH = 500;

export const RESOLVE_STATUS_COLORS = {
  open: '#AEA9B1',
  others: '#DDFCAD',
  won: '#78B159',
  lost: '#FB8C8C',
  void: '#947BE7',
  refunded: '#67C6FF',
  cancelled: '#FFB86A',
  tie: '#0062CC',
  half_won: ['#0062CC', '#78B159'],
  half_lost: ['#0062CC', '#FB8C8C'],
  cashout: '#2c65ba',
} as Partial<Record<BetStatus, string | string[]>>;

export const RESOLVE_STATUS_TEXT_COLORS = {
  open: 'inherit',
  won: 'success.light',
  lost: 'error.400',
  void: '#947BE7',
  refunded: '#67C6FF',
  cancelled: '#FFB86A',
  cashout: '#F7B61E',
  others: 'secondary.main',
} as Record<BetslipStatus, string>;

export const getBetHistoryActionCellMap = (t: TFunction) =>
  ({
    lost: {
      text: t('lost'),
      color: RESOLVE_STATUS_COLORS.lost,
    },

    won: {
      text: t('won'),
      color: RESOLVE_STATUS_COLORS.won,
    },

    void: {
      text: t('void'),
      color: RESOLVE_STATUS_COLORS.void,
    },

    refunded: {
      text: t('refunded', 'Refunded'),
      color: RESOLVE_STATUS_COLORS.refunded,
    },

    cancelled: {
      text: t('cancelled', 'Cancelled'),
      color: RESOLVE_STATUS_COLORS.cancelled,
    },

    open: {
      text: t('open'),
      color: RESOLVE_STATUS_COLORS.open,
    },

    cashout: {
      text: t('cashedOut', 'Cashed Out'),
      color: RESOLVE_STATUS_COLORS.cashout,
    },

    others: {
      text: t('jackpot'),
      color: RESOLVE_STATUS_COLORS.others,
    },
  }) as Record<BetslipStatus, { text: string; color: string }>;

export const BET_HISTORY_TAB_CONFIG = [
  { label: 'all', value: 'all' },
  { label: 'openBets', value: 'open' },
  { label: 'won', value: 'won' },
  { label: 'lost', value: 'lost' },
  { label: 'cashOut', value: 'cashout' },
  { label: 'others', value: 'others' },
];

export const GOLA_BET_HISTORY_TAB_CONFIG = [{ label: 'sports', value: 'all' }];

export const SCOREBOARD_TAB_CONFIG = [{ label: 'scoreboard', value: 'scoreboard' }];

export const UPCOMING_MATCHES_TAB_CONFIG = [
  { label: 'topLeagues', value: 'top-leagues' },
  { label: 'allMatches', value: 'all-matches' },
  { label: 'schedule', value: 'schedule' },
];

export const ODD_CHANGES: MenuItemType[] = [
  {
    key: 'all',
    label: 'acceptAll',
  },
  {
    key: 'none',
    label: 'acceptNone',
  },
  {
    key: 'rising',
    label: 'acceptHigher',
  },
];

export const ConditionModelTypeEnum = {
  sport: 'sport',
  competition: 'competition',
  tournament: 'tournament',
  marketType: 'market_type',
  event: 'event',
  punter: 'punter',
  ticket: 'ticket',
} as const;

export const PUNTER_COUNTDOWN_DURATION = 30;

export const WS_EVENTS_ENUMS = {
  outcome: 'OUTCOME',
  event: 'EVENT',
  market: 'MARKET',
  eventPartScore: 'EVENT_PART_SCORE',
} as const;

export const WALLET_TYPE_OPTIONS = [
  {
    value: WALLET_TYPE.MAIN,
    label: 'mainBalance',
  },
  {
    value: WALLET_TYPE.BONUS,
    label: 'bonusCredits',
  },
];

export const BANKER_MENU_OPTIONS = [
  { value: 'normal', label: 'normal' },
  { value: 'bankers', label: 'bankers' },
];

export const CURRENCY = {
  code: 'EUR',
  symbol: '€',
};

export const MARKET_TYPE_ASIAN_HANDICAP_WITH_SCORE = 'Asian Handicap With Score';

export const EVENT_TYPE_TO_PATH_MAP = {
  match: 'matches',
  outright: 'outrights',
};

export const BETSLIP_TICKET_TYPE = {
  IN_PLAY: 'inPlay',
  PRE_MATCH: 'preMatch',
  MIXED: 'mixed',
} as const;

export const BETSLIP_FROM = {
  PRE_MATCH: 'Pre-match',
  IN_PLAY: 'In-play',
  MIX: 'Mix',
} as const;

export const USED_FOR = {
  IN_PLAY: 'in_play',
  PRE_MATCH: 'pre_match',
} as const;

export const SPORT_ICON_POSITION: {
  [key: string]: string;
} = {
  '1': ' -17px',
  '3': '-168px',
  '6': '-101px',
  '7': '-66px',
  '8': '-51px',
  '9': '-33px',
  '20': '-118px',
  '22': '-66px',
  '26': '-84px',
  '28': '-33px',
  '41': '-101px',
  '49': '-51px',
  '63': '-135px',
};

export const FTB_SPORT_ICON_POSITION: {
  [key: string]: string;
} = {
  '1': ' -17px',
  '2': '-51px',
  '3': '-168px',
  '4': '-118px',
  '5': '-101px',
  '6': '-135px',
  '7': '-66px',
  '9': '-33px',
};

export const DEFAULT_MY_ACCOUNT_FIELDS = [
  'country',
  'currency',
  'phone',
  'firstName',
  'lastName',
  'dateOfBirth',
  'email',
];

export { API_ROUTES } from './apiRoutes';
